/**
 * Student Portal — Home page.
 * Greeting + summary stats + quick actions.
 */
import React from 'react';
import { Plus, Package, RotateCcw, ClipboardList, Layers } from 'lucide-react';
import { Card, StatusPill, orderStatusPill } from '../ui';

const timeGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export default function HomePage({ ctx }) {
  const { user, counts, orders, goto, openNewRequest } = ctx;
  const firstName = (user?.full_name || user?.username || '').split(' ')[0] || (user?.username || 'there');

  const recentRequests = orders.slice(0, 3);

  return (
    <>
      <div>
        <span className="sp-eyebrow">Home</span>
        <h1 className="sp-page-greeting" data-testid="portal-home-greeting">
          {timeGreeting()}, {firstName}.
        </h1>
        <p className="sp-page-lede" data-testid="portal-home-lede">
          Manage your implant components, requests and returns.
        </p>
        <p
          data-testid="portal-home-institution"
          style={{
            margin: '6px 0 0',
            fontSize: 12,
            letterSpacing: '0.02em',
            color: 'var(--diis-ink-subtle)',
            fontWeight: 400,
            lineHeight: 1.5,
          }}
        >
          Department of Implantology · Saveetha Dental College &amp; Hospitals
        </p>
      </div>

      {/* ── Summary stats ───────────────────────────────── */}
      <div className="sp-stat-row" data-testid="portal-home-stats">
        <div className="sp-stat">
          <span className="sp-stat-label">My Kit</span>
          <span className="sp-stat-value sp-mono-num" data-testid="stat-kit">{counts.kitCount}</span>
          <span className="sp-stat-hint">
            {counts.kitCount === 0 ? 'No components currently with you' :
             counts.kitCount === 1 ? '1 component currently with you' :
             `${counts.kitCount} components currently with you`}
          </span>
        </div>
        <div className="sp-stat">
          <span className="sp-stat-label">Pending Requests</span>
          <span className="sp-stat-value sp-mono-num" data-testid="stat-pending">{counts.pendingRequests}</span>
          <span className="sp-stat-hint">
            {counts.pendingRequests === 0 ? 'Nothing awaiting approval or issuance' : 'Awaiting faculty approval or clerk issuance'}
          </span>
        </div>
        <div className="sp-stat">
          <span className="sp-stat-label">To Return</span>
          <span className="sp-stat-value sp-mono-num" data-testid="stat-toreturn">{counts.toReturn}</span>
          <span className="sp-stat-hint">
            {counts.toReturn === 0 ? 'Nothing due for return' : 'Issued items pending return'}
          </span>
        </div>
      </div>

      {/* ── Quick actions ───────────────────────────────── */}
      <div className="sp-quick-grid">
        <button
          type="button"
          className="sp-quick sp-quick-primary"
          onClick={() => openNewRequest()}
          data-testid="qa-request"
        >
          <span className="sp-quick-eyebrow"><Plus size={12} strokeWidth={2.4} style={{ verticalAlign: -1 }} /> New Request</span>
          <span className="sp-quick-title">Request a Component</span>
          <span className="sp-quick-desc">
            Search inventory, add a patient and route to your faculty for approval — usually the fastest way to get a component.
          </span>
        </button>

        <button type="button" className="sp-quick" onClick={() => goto('returns')} data-testid="qa-returns">
          <span className="sp-quick-eyebrow"><RotateCcw size={12} strokeWidth={2.4} style={{ verticalAlign: -1 }} /> Return</span>
          <span className="sp-quick-title">Return Components</span>
          <span className="sp-quick-desc">Log an item back to inventory and hand it to the clerk for verification.</span>
        </button>

        <button type="button" className="sp-quick" onClick={() => goto('kit')} data-testid="qa-kit">
          <span className="sp-quick-eyebrow"><Package size={12} strokeWidth={2.4} style={{ verticalAlign: -1 }} /> My Kit</span>
          <span className="sp-quick-title">My Clinical Kit</span>
          <span className="sp-quick-desc">Every component currently issued to you, with size, brand and due-back date.</span>
        </button>

        <button type="button" className="sp-quick" onClick={() => goto('requests')} data-testid="qa-requests">
          <span className="sp-quick-eyebrow"><ClipboardList size={12} strokeWidth={2.4} style={{ verticalAlign: -1 }} /> Requests</span>
          <span className="sp-quick-title">My Requests</span>
          <span className="sp-quick-desc">Track every request from Pending through Approved, Issued and Returned.</span>
        </button>
      </div>

      {/* ── Recent activity ─────────────────────────────── */}
      <div className="sp-mt-6">
        <Card
          title="Recent Requests"
          subtitle="Your three most recent submissions."
          actions={<button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => goto('requests')} data-testid="home-view-all-requests">View all</button>}
          testId="home-recent-requests"
        >
          {recentRequests.length === 0 ? (
            <p className="sp-muted" style={{ margin: 0, fontSize: 13 }}>
              You haven't made any requests yet. Use <em>Request a Component</em> above to submit your first one.
            </p>
          ) : (
            recentRequests.map(o => {
              const pill = orderStatusPill(o.status);
              return (
                <div key={o.id} className="sp-item-line" style={{ padding: '10px 0', borderBottom: '1px solid var(--diis-hairline)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13 }}>
                      <strong>{o.patient_info?.name || '—'}</strong>
                      <span className="sp-subtle" style={{ marginLeft: 8 }}>
                        {(o.items || []).length} item{(o.items || []).length === 1 ? '' : 's'} · REQ-{o.id.slice(-6).toUpperCase()}
                      </span>
                    </div>
                    <div className="sp-subtle" style={{ marginTop: 2 }}>
                      {new Date(o.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                </div>
              );
            })
          )}
        </Card>
      </div>
    </>
  );
}
